// Resolving which DSP an audience belongs to, and how to label it.
//
// The DSP lives in two places. `audience.dsp` is the record-level column
// (LIQUIDM or NEXXEN -- the enum has no other values). Individual entries in
// `dspSegmentIds` may ALSO carry a `dsp`, and when they do it wins: a record can
// hold a stale top-level value while the segment it actually published to says
// otherwise.
//
// The subtlety that made every new-pipeline audience render "N/A": the
// new Nexxen pipeline writes `{ segmentId, segmentName, jobId }` with no `dsp`
// key at all. Code that picked the segment value based on the ARRAY being
// non-empty -- `segments.length ? segments[0].dsp : audience.dsp` -- therefore
// resolved to undefined and never reached the fallback, even though
// `audience.dsp` was correctly set to "NEXXEN". The presence of the `dsp` key
// is what matters, not the presence of a segment.

/** The audience's effective DSP name, or undefined if genuinely unknown. */
export const resolveDspName = (audience) => {
  if (!audience || typeof audience !== "object") return undefined;

  const segments = Array.isArray(audience.dspSegmentIds) ? audience.dspSegmentIds : [];
  const first = segments.length && typeof segments[0] === "object" ? segments[0] : null;

  return (first && first.dsp) || audience.dsp || undefined;
};

/**
 * Maps a DSP name to what the current viewer is allowed to see. White-label
 * orgs (providersVisible false) get tier labels instead of partner names.
 */
export const getDSPDisplayName = (dspName, providersVisible) => {
  if (providersVisible) {
    return dspName; // Show actual DSP name
  }

  switch (dspName?.toLowerCase()) {
    case "liquidm":
      return "Tier 1";
    case "nexxen":
      return "Tier 2";
    default:
      return dspName; // Return original if not mapped
  }
};

/** The DSP string to render in a table cell, already cased and defaulted. */
export const formatDspCell = (audience, providersVisible) => {
  const displayName = getDSPDisplayName(resolveDspName(audience), providersVisible);
  return displayName && typeof displayName === "string"
    ? displayName.toUpperCase()
    : "N/A";
};
